/**
 * What the advisor sees while a customer decides. Deliberately offers no way past it — Cancel
 * abandons the request without unlocking the questionnaire, and the card says so.
 */

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Spinner,
} from '@design-system';

import { AlertCircle, Check, ExternalLink, MailCheck, RefreshCw, X } from 'lucide-react';
import { SurveySubmissionSource } from '@shared/surveySubmissionSource';

import { useAgentEnteredIntakeStatus } from '@/features/invite-client';

interface AgentEnteredWaitingCardProps {
  requestId: string;
  /** Rendered when the advisor starts over, so the caller can clear its own state. */
  onDismiss?: () => void;
  /** Called instead of opening a tab, for flows that continue in place (Quick Plan). */
  onConsented?: (requestId: string) => void;
  consentedActionLabel?: string;
}

function formatSentAt(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function resendBlockedReason(request: {
  canResend: boolean;
  resendAvailableAt: string | null;
  sentCount: number;
}): string | null {
  if (request.canResend) return null;
  if (request.resendAvailableAt) {
    const at = new Date(request.resendAvailableAt);
    if (!Number.isNaN(at.getTime())) {
      return `You can send this again after ${at.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })}.`;
    }
  }
  return 'This has been sent the maximum number of times.';
}

export function AgentEnteredWaitingCard({
  requestId,
  onDismiss,
  onConsented,
  consentedActionLabel = 'Open Intake Form',
}: AgentEnteredWaitingCardProps) {
  const { request, isLoading, isConsented, resend, isResending, cancel, isCancelling } =
    useAgentEnteredIntakeStatus(requestId);

  if (isLoading || !request) {
    return (
      <Card className="shadow-none">
        <CardContent className="flex items-center justify-center py-10">
          <Spinner className="text-primary size-6" />
        </CardContent>
      </Card>
    );
  }

  const sentAt = formatSentAt(request.lastSentAt);
  const blockedReason = resendBlockedReason(request);

  function handleConsentedAction() {
    if (onConsented) {
      onConsented(requestId);
      return;
    }
    if (!request?.formReferenceId) return;
    window.open(
      `${window.location.origin}/onboarding-form/${request.formReferenceId}?source=${SurveySubmissionSource.FILL_FORM}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  if (isConsented) {
    return (
      <Card className="shadow-none">
        <CardContent className="space-y-4 py-6">
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-green-50">
              <Check
                className="size-5 text-green-600"
                aria-hidden="true"
              />
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-950">
                {request.clientFirstName} gave permission
              </h4>
              <p className="text-muted-foreground text-sm">
                You can now complete the questionnaire on their behalf.
              </p>
            </div>
          </div>

          <Button
            className="w-full"
            onClick={handleConsentedAction}
            disabled={!onConsented && !request.formReferenceId}
          >
            <ExternalLink className="size-4" />
            {consentedActionLabel}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (request.status === 'cancelled') {
    return (
      <Card className="shadow-none">
        <CardContent className="space-y-4 py-6">
          <h4 className="text-base font-semibold text-gray-950">Request cancelled</h4>
          <p className="text-muted-foreground text-sm">
            Cancelling does not unlock the questionnaire. Start a new request when you&apos;re
            ready.
          </p>
          {onDismiss && (
            <Button
              variant="outline"
              className="w-full"
              onClick={onDismiss}
            >
              Start over
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-none">
      <CardContent className="space-y-4 py-6">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
            <MailCheck
              className="text-primary size-5"
              aria-hidden="true"
            />
          </div>
          <div>
            <h4 className="text-base font-semibold text-gray-950">
              {request.isUnsent
                ? `${request.clientFirstName} hasn't been emailed yet`
                : `Waiting for ${request.clientFirstName} to give permission`}
            </h4>
            <p className="text-muted-foreground text-sm">
              {request.isUnsent ? 'For ' : 'Sent to '}
              {request.sentTo}
              {sentAt ? ` · ${sentAt}` : ''}
            </p>
          </div>
        </div>

        {request.status === 'undeliverable' && (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>We couldn&apos;t email this address</AlertTitle>
            <AlertDescription>
              It has unsubscribed from Waterlily emails, so nothing was sent. Ask your client for
              another address and start a new request.
            </AlertDescription>
          </Alert>
        )}

        {request.isUnsent && (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>We couldn&apos;t send that email</AlertTitle>
            <AlertDescription>
              Nothing reached your client. Send it again — this has not used up any of your
              attempts.
            </AlertDescription>
          </Alert>
        )}

        {request.isExpired && (
          <Alert variant="info">
            <AlertCircle />
            <AlertTitle>That link has expired</AlertTitle>
            <AlertDescription>Send it again to give your client a fresh one.</AlertDescription>
          </Alert>
        )}

        <p className="text-muted-foreground text-sm">
          You can&apos;t open the questionnaire until they accept.
          {!request.isUnsent &&
            ' Resending replaces the previous link, so the older email will stop working.'}
        </p>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            className="flex-1"
            disabled={!request.canResend || isResending}
            onClick={() => resend()}
          >
            {isResending ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
            Resend
          </Button>
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive flex-1"
            disabled={isCancelling}
            onClick={() => cancel()}
          >
            <X className="size-4" />
            Cancel
          </Button>
        </div>

        {blockedReason && <p className="text-muted-foreground text-xs">{blockedReason}</p>}
      </CardContent>
    </Card>
  );
}
